// services/notification.service.ts - PRODUCTION READY WITH NEW ENDPOINT
import { HttpContext } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { authBackendEndPointToken } from '../app.config';
import { DisableCache, LoadingUIEnabled } from '../loading.interceptor';

export interface NotificationItem {
  id: string;
  type: number;
  title: string;
  message: string;
  triggeredByResourceId: string;
  triggeredByResourceType: string;
  isRead: boolean;
  createdAt: string;
  readAt?: string;
  resourceLink?: string;
}

export interface NotificationStats {
  unreadCount: number;
  totalCount: number;
  countByType: Record<number, number>;
}

export interface NotificationListResponse {
  count: number;
  skip: number;
  data: NotificationItem[];
}

export interface GenericNotificationRequest {
  resourceType: string;
  resourceId: string;
  organizationId: string;
  targetRoles: string[];
  title?: string;
  message?: string;
  sendEmail?: boolean;
  data?: Record<string, any>;
}

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private hubConnection: signalR.HubConnection | null = null;
  private destroy$ = new Subject<void>();
  backendApi = inject(authBackendEndPointToken);

  private unreadCount$ = new BehaviorSubject<number>(0);
  private notifications$ = new BehaviorSubject<NotificationItem[]>([]);
  private isConnected$ = new BehaviorSubject<boolean>(false);
  private newNotification$ = new Subject<NotificationItem>();

  readonly unreadCount = this.unreadCount$.asObservable();
  readonly notifications = this.notifications$.asObservable();
  readonly isConnected = this.isConnected$.asObservable();
  readonly newNotification = this.newNotification$.asObservable();

  private apiBase = `${this.backendApi}/api/notifications`;

  constructor(private http: HttpClient) {}

  /** Call this after user logs in */
  startConnection(token: string): void {
    if (this.hubConnection) {
      console.log('ℹ️ SignalR already connected, skipping duplicate connection');
      return;
    }

    console.log('🔌 Starting SignalR connection...');

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.backendApi}/hubs/notifications`, {
        accessTokenFactory: () => token,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        skipNegotiation: false,
        withCredentials: true,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds > 600000) return null;
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount < 5) {
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 10000);
          }
          return 30000;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // ✅ HANDLER 1: Receive real-time notifications (camelCase from server)
    this.hubConnection.on('receiveNotification', (payload: NotificationItem) => {
      console.log('📬 Real-time notification received:', payload);
      this.handleIncomingNotification(payload);
    });

    // ✅ HANDLER 2: Update unread count
    this.hubConnection.on('unreadCountUpdate', (count: number) => {
      console.log('📊 Unread count updated:', count);
      this.unreadCount$.next(count);
    });

    // ✅ HANDLER 3: Connection established confirmation
    this.hubConnection.on('connectionEstablished', (data: any) => {
      console.log('✅ SignalR connection established:', data);
      this.isConnected$.next(true);
    });

    this.hubConnection.onreconnecting(() => {
      console.log('🔄 SignalR attempting to reconnect...');
      this.isConnected$.next(false);
    });

    this.hubConnection.onreconnected(() => {
      console.log('✅ SignalR reconnected successfully');
      this.isConnected$.next(true);
      // ✅ Only reload unread count after reconnect, NOT the full list
      //this.loadUnreadCount();
    });

    this.hubConnection.onclose(() => {
      console.log('❌ SignalR connection closed');
      this.isConnected$.next(false);
    });

    // Start the connection
    this.hubConnection
      .start()
      .then(() => {
        console.log('✅ SignalR started successfully');
        this.isConnected$.next(true);
        // ✅ Load initial unread count
        //this.loadUnreadCount();
        // ✅ Don't auto-load notifications - let components load when needed
      })
      .catch((err: any) => {
        console.error('❌ SignalR connection failed:', err);
        this.isConnected$.next(false);
      });
  }

  stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection.stop();
      this.hubConnection = null;
      this.isConnected$.next(false);
      console.log('🛑 SignalR connection stopped');
    }
  }

  private handleIncomingNotification(notification: NotificationItem): void {
    console.log('Processing incoming notification:', notification);

    // ✅ Check if notification already exists (avoid duplicates)
    const current = this.notifications$.value;
    const exists = current.some(n => n.id === notification.id);
    
    if (exists) {
      console.log('⚠️ Notification already exists, skipping duplicate:', notification.id);
      return;
    }

    // ✅ Add to the top of the list
    const updated = [notification, ...current];
    this.notifications$.next(updated);

    // ✅ The server sends unreadCountUpdate, so we don't manually increment here

    // ✅ Emit for toast/sound triggers
    this.newNotification$.next(notification);

    console.log('✅ Notification added to UI');
  }

  loadUnreadCount(): void {
    this.http.get<{ unreadCount: number }>(`${this.apiBase}/unread-count`, {
      context: new HttpContext().set(DisableCache, true).set(LoadingUIEnabled, false)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          console.log('📊 Unread count loaded:', res.unreadCount);
          this.unreadCount$.next(res.unreadCount);
        },
        error: (err) => {
          console.error('❌ Failed to load unread count:', err);
        }
      });
  }

  loadRecentNotifications(count = 10, skip = 0): Observable<NotificationListResponse> {
    const obs = this.http.get<NotificationListResponse>(
      `${this.apiBase}/recent?count=${count}&skip=${skip}`,
      {
        context: new HttpContext().set(DisableCache, true).set(LoadingUIEnabled, false)
      }
    );
    
    // ✅ Only update the BehaviorSubject if this is the initial load (skip === 0)
    // For pagination (skip > 0), let the component handle appending
    if (skip === 0) {
      obs.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            console.log('📋 Recent notifications loaded:', res.data.length);
            this.notifications$.next(res.data);
          },
          error: (err) => {
            console.error('❌ Failed to load recent notifications:', err);
          }
        });
    }

    return obs;
  }
// services/notification.service.ts - Add role-based methods

/**
 * Load unread count for specific role
 */
loadUnreadCountForRole(role: string): void {
  this.http.get<{ unreadCount: number; role: string }>(
    `${this.apiBase}/unread-count-by-role?role=${role}`,
    {
      context: new HttpContext().set(DisableCache, true).set(LoadingUIEnabled, false)
    }
  )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (res) => {
        console.log(`📊 Unread count for role ${role}:`, res.unreadCount);
        this.unreadCount$.next(res.unreadCount);
      },
      error: (err) => {
        console.error(`❌ Failed to load unread count for role ${role}:`, err);
      }
    });
}

/**
 * Load recent notifications filtered by role
 */
loadRecentNotificationsByRole(role: string, count = 10, skip = 0): Observable<NotificationListResponse> {
  const obs = this.http.get<NotificationListResponse>(
    `${this.apiBase}/recent-by-role?role=${role}&count=${count}&skip=${skip}`,
    {
      context: new HttpContext().set(DisableCache, true).set(LoadingUIEnabled, false)
    }
  );
  
  if (skip === 0) {
    obs.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          console.log(`📋 Notifications for role ${role}:`, res.data.length);
          this.notifications$.next(res.data);
        },
        error: (err) => {
          console.error(`❌ Failed to load notifications for role ${role}:`, err);
        }
      });
  }

  return obs;
}
  /**
   * Get notification statistics
   * GET /api/notifications/statistics
   */
  getNotificationStats(): Observable<NotificationStats> {
    return this.http.get<NotificationStats>(`${this.apiBase}/statistics`, {
      context: new HttpContext().set(DisableCache, true)
    })
    .pipe(takeUntil(this.destroy$));
  }

  /**
   * Get notifications by resource
   * GET /api/notifications/by-resource?resourceId=123&resourceType=Appointment
   */
  getNotificationsByResource(resourceId: string, resourceType: string): Observable<NotificationListResponse> {
    return this.http.get<NotificationListResponse>(
      `${this.apiBase}/by-resource?resourceId=${resourceId}&resourceType=${resourceType}`,
      {
        context: new HttpContext().set(DisableCache, true)
      }
    )
    .pipe(takeUntil(this.destroy$));
  }

  /**
   * Trigger a generic notification
   * POST /api/notifications/trigger
   * 
   * @example
   * // Notify pharmacy staff about new medication request
   * triggerNotification({
   *   resourceType: 'MedicationRequest',
   *   resourceId: '123-456-789',
   *   organizationId: 'org-123',
   *   targetRoles: ['pharmacist', 'pharmacy'],
   *   title: 'New Prescription',
   *   message: 'New prescription ready for review',
   *   sendEmail: true
   * })
   * 
   * @example
   * // Notify lab staff about new service request
   * triggerNotification({
   *   resourceType: 'ServiceRequest',
   *   resourceId: '456-789-012',
   *   organizationId: 'org-123',
   *   targetRoles: ['lab', 'labtechnician'],
   *   title: 'New Lab Request',
   *   message: 'New lab test requested'
   * })
   */
  triggerNotification(request: GenericNotificationRequest): Observable<any> {
    return this.http.post(`${this.apiBase}/trigger`, request)
      .pipe(takeUntil(this.destroy$));
  }

  markAsRead(notificationId: string): Observable<unknown> {
    return new Observable(observer => {
      this.http.post(`${this.apiBase}/${notificationId}/mark-as-read`, {})
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            // ✅ Update local state
            const updated = this.notifications$.value.map(n =>
              n.id === notificationId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            );
            this.notifications$.next(updated);

            // ✅ Reload unread count from server for accuracy
            this.loadUnreadCount();

            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            console.error('❌ Failed to mark as read:', err);
            observer.error(err);
          }
        });
    });
  }

  markAllAsRead(): Observable<unknown> {
    return new Observable(observer => {
      this.http.post(`${this.apiBase}/mark-all-as-read`, {})
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            // ✅ Update local state
            const updated = this.notifications$.value.map(n => ({
              ...n,
              isRead: true,
              readAt: new Date().toISOString()
            }));
            this.notifications$.next(updated);
            
            // ✅ Set unread count to 0
            this.unreadCount$.next(0);
            
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            console.error('❌ Failed to mark all as read:', err);
            observer.error(err);
          }
        });
    });
  }

  deleteNotification(notificationId: string): Observable<unknown> {
    return new Observable(observer => {
      this.http.delete(`${this.apiBase}/${notificationId}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            // ✅ Remove from local state
            const updated = this.notifications$.value.filter(n => n.id !== notificationId);
            this.notifications$.next(updated);
            
            // ✅ Recalculate unread count
            const unread = updated.filter(n => !n.isRead).length;
            this.unreadCount$.next(unread);
            
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            console.error('❌ Failed to delete notification:', err);
            observer.error(err);
          }
        });
    });
  }

  /**
   * Delete all notifications for current user
   * DELETE /api/notifications/all
   */
  deleteAllNotifications(): Observable<any> {
    return new Observable(observer => {
      this.http.delete(`${this.apiBase}/all`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            // ✅ Clear local state
            this.notifications$.next([]);
            this.unreadCount$.next(0);
            
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            console.error('❌ Failed to delete all notifications:', err);
            observer.error(err);
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopConnection();
  }
}